import { Component, ElementRef, HostListener, Inject, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { AppState } from 'src/app/core/app.state';
import { Actions } from '@ngrx/effects';
import { ICurrencyPrice, IVariant } from '../../models/products.models';
import { TranslateService } from '@ngx-translate/core';
import { CommonService } from 'src/app/shared/common.service';
import { environment } from 'src/environments/environment';
import { filter, Observable, of, take } from 'rxjs';
import { ICurrency } from 'src/app/features/customers/models/customers.models';
import { FetchAllCurrencies } from 'src/app/features/customers/store/customers.actions';

interface AddedCurrencyControl {
  data: ICurrency;
  variantId?: number;
  controls: {
    retail_price: FormGroup;
    cost: FormGroup;
  };
}
@Component({
  selector: 'app-edit-product-variants',
  templateUrl: './edit-product-variants.component.html',
  styleUrls: ['./edit-product-variants.component.scss'],
})
export class EditProductVariantsComponent implements OnInit {
  @ViewChild('dropdown') dropdownRef!: ElementRef;
  @ViewChild('button') buttonRef!: ElementRef;
  currencies$: Observable<ICurrency[]> = of([]);
  default_currency!: ICurrency | null | undefined;
  currencies: ICurrency[] = [];
  availableCurrencies: ICurrency[] = [];
  addedCurrencies: AddedCurrencyControl[] = [];
  showCurrencyDropdown = false;
  currenciesDropdown = {
    top: 0,
    left: 0,
    minWidth: 0,
  };
  form!: FormGroup;
  variants: any[] = [];
  attributes: any[] = [];
  defaultVariantAvatar: string = '../../../../../assets/icones/drop_variant.svg';
  isDisabled = environment.BASE_URL_API.includes('stareuropa');
  timestamp = new Date().getTime();
  failedImages = new Set<string>();
  currenciesToDisplay: ICurrencyPrice[] = [];
  constructor(
    public dialogRef: MatDialogRef<EditProductVariantsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: FormBuilder,
    private store: Store<AppState>,
    private dialog: MatDialog,
    private actions$: Actions,
    private translateService: TranslateService,
    private commonService: CommonService
  ) {
    this.form = this.fb.group({});
  }

  ngOnInit(): void {
    this.data.variants.forEach((variant: IVariant) => {
      this.form.addControl(`sku_${variant.id}`, new FormControl(variant.sku ?? '', [Validators.required]));
      this.form.addControl(`bar_code_${variant.id}`, new FormControl(variant.barcode ?? '', []));
      this.form.addControl(`cost_${variant.id}`, new FormControl(variant.cost ?? '', []));
      this.form.addControl(`sales_price_${variant.id}`, new FormControl(variant.sales_price ?? '', []));
      this.form.addControl(`quantity_${variant.id}`, new FormControl({ value: variant.stock?.quantity ?? '', disabled: this.isDisabled }));
      this.form.addControl(`reorder_point_${variant.id}`, new FormControl(variant.stock?.reorder_point ?? '', []));
      this.form.addControl(`reorder_quantity_${variant.id}`, new FormControl(variant.stock?.reorder_quantity ?? '', []));
      this.form.addControl(`expiry_date_${variant.id}`, new FormControl(variant.expiry_date ?? '', []));
      this.form.addControl(`enabled_${variant.id}`, new FormControl(variant.enabled ?? true, []));
      this.form.addControl(`increment_${variant.id}`, new FormControl(variant.increment ?? 1, []));
      this.form.addControl(`max_qte_${variant.id}`, new FormControl(variant.max_qte ?? null, []));
      this.form.addControl(`min_qte_${variant.id}`, new FormControl(variant.min_qte ?? 1, []));
      const codesArray = this.fb.array<FormGroup>([]);
      this.form.addControl(`codes_${variant.id}`, codesArray);
      if (variant.codes) {
        variant.codes.forEach((code) => {
          codesArray.push(
            this.fb.group({
              label: new FormControl(code.label ?? '', [Validators.required]),
              value: new FormControl(code.value ?? '', [Validators.required]),
            })
          );
        });
      }
      variant.currency_prices?.forEach((price: ICurrencyPrice) => {
        if(!this.currenciesToDisplay.map(c => c.currency_id).includes(price.currency_id!)) this.currenciesToDisplay.push(price);
      });
    });
    this.variants = this.data.variants.map((variant: IVariant) => {
      return {
        ...variant,
      };
    });
    this.attributes = this.data.attributes;
    this.store.dispatch(FetchAllCurrencies());
    this.store
      .select((state) => state.customers.allCurrencies)
      .pipe(
        filter((currencies) => Array.isArray(currencies) && currencies.length > 0),
        take(1)
      )
      .subscribe((currencies) => {
        this.handleCurrencies(currencies);
      });
  }

  private handleCurrencies(currencies: ICurrency[]): void {
    this.addedCurrencies = [];

    this.default_currency = currencies.find((currency) => currency.is_default);

    this.currencies = currencies.filter((currency) => !currency.is_default);

    const usedCurrencyIds = new Set<number>();

    this.data.variants.forEach((variant: IVariant) => {
      variant?.currency_prices?.forEach((price: ICurrencyPrice) => {
        if (price.currency_id != null) {
          usedCurrencyIds.add(price.currency_id);
        }
      });
    });

    this.availableCurrencies = this.currencies.filter((currency) => !usedCurrencyIds.has(currency.id));

    this.currencies.forEach((currency: ICurrency) => {
      if (usedCurrencyIds.has(currency.id)) {
        this.addCurrency(currency);
      }
    });
  }

  getCurrencyName(currencyId: number): string {
    return this.currencies.find(c => c.id === currencyId)?.name ?? '';
  }
  getCurrencySymbol(currencyId: number): string {
    return this.currencies.find(c => c.id === currencyId)?.symbol ?? '';
  }

  getImage(product: any) {
    return product.image
      ? product.image.includes('.jpg')
        ? environment.BASE_URL_API + product.image + '?' + this.timestamp
        : `data:'';base64, ${product.image}`
      : this.defaultVariantAvatar;
  }

  onImageError(imageUrl: string): void {
    this.failedImages.add(imageUrl); // Mark this image as failed
  }

  getVariantImg(event: any, index: number) {
    let file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const dataUrl = e.target.result;
        this.variants[index].image = dataUrl.split(',')[1];
      };
      reader.readAsDataURL(file);
    }
  }
  cancelImageVariant(index: number) {
    this.variants[index].image = '';
    // this.variants.find(variant => variant.id === variantId)!.image = '';
  }

  getCodesArray(variantId: number): FormArray<FormGroup> | null {
    return this.form.get(`codes_${variantId}`) as FormArray<FormGroup>;
  }

  addCodeToVariant(variantId: number): void {
    const codesArray = this.getCodesArray(variantId);
    if (codesArray) {
      codesArray.push(
        this.fb.group({
          label: ['', Validators.required],
          value: ['', Validators.required],
        })
      );
    }
  }

  removeVariantCode(variantId: number, index: number): void {
    const codesArray = this.getCodesArray(variantId);
    if (codesArray) {
      codesArray.removeAt(index);
    }
  }

  sanitize(event: any) {
    event.target.value = event.target.value.replace(/[^\d]/g, '');
  }

  sanitizePrice(event: any) {
    const input = event.target;
    if (input.value < 0) {
      input.value = 0;
    }
  }

  save(): boolean | void {
    if (!this.form.valid) {
      this.commonService.showToast(this.translateService.instant('inventory.alert_variant_form_invalid'), 'WARNING');
      return false;
    }
    if (!this.validateVariantsQuantityBeforeSave()) {
      return false;
    }
    const variants = this.variants.map((variant: IVariant) => {
      const currency_prices = this.addedCurrencies
        .filter(c => c.variantId === variant.id)
        .flatMap(c => [
          c.controls.retail_price.value,
          c.controls.cost.value
        ]);
      const payload: any = {
        id: variant.id,
        values: variant.values,
        sku: this.form.get(`sku_${variant.id}`)?.value,
        barcode: this.form.get(`bar_code_${variant.id}`)?.value,
        cost: this.form.get(`cost_${variant.id}`)?.value,
        sales_price: this.form.get(`sales_price_${variant.id}`)?.value,
        expiry_date: this.form.get(`expiry_date_${variant.id}`)?.value,
        increment: this.form.get(`increment_${variant.id}`)?.value,
        max_qte: this.form.get(`max_qte_${variant.id}`)?.value,
        min_qte: this.form.get(`min_qte_${variant.id}`)?.value,
        stock: {
          quantity: this.form.get(`quantity_${variant.id}`)?.value,
          reorder_point: this.form.get(`reorder_point_${variant.id}`)?.value,
          reorder_quantity: this.form.get(`reorder_quantity_${variant.id}`)?.value,
        },
        currency_prices: currency_prices,
        image: variant.image,
        codes: this.form.get(`codes_${variant.id}`)?.value,
        enabled: this.form.get(`enabled_${variant.id}`)?.value,
        code_identifiers: this.form.get(`codes_${variant.id}`)?.value.reduce((acc: any, code: any) => {
          acc[code.label] = code.value;
          return acc;
        }, {}),
      };

      return payload;
    });
    this.dialogRef.close({variants, addedCurrencies: this.addedCurrencies});
  }

  closed() {
    this.dialogRef.close();
  }

  validateVariantsQuantityBeforeSave(): boolean {
    for (const variant of this.variants) {
      const min = this.form.get(`min_qte_${variant.id}`)?.value;
      const max = this.form.get(`max_qte_${variant.id}`)?.value;
      const inc = this.form.get(`increment_${variant.id}`)?.value;
      const sku = this.form.get(`sku_${variant.id}`)?.value;

      if (min != null && min < 1) {
        this.commonService.showToast(this.translateService.instant('Catalogs.validators.min_qte_greater_equal_one') + ' SKU: ' + sku, 'WARNING');
        return false;
      }

      if (inc != null && inc < 1) {
        this.commonService.showToast(this.translateService.instant('Catalogs.validators.increment_greater_equal_one') + ' SKU: ' + sku, 'WARNING');
        return false;
      }

      if (min != null && max != null && min >= max) {
        this.commonService.showToast(this.translateService.instant('Catalogs.validators.min_less_than_max') + ' SKU: ' + sku, 'WARNING');
        return false;
      }

      if (inc != null && min != null && inc > min) {
        this.commonService.showToast(
          this.translateService.instant('Catalogs.validators.increment_less_than_or_equal_min') + 'SKU: ' + sku,
          'WARNING'
        );
        return false;
      }
    }

    return true;
  }

  addCurrency(currency: ICurrency): void {
    const createControls = (currency: ICurrency, variant_id: number = 0, retail_amount: number | null = null, cost_amount: number | null = null) => ({
      retail_price: this.fb.group({
        currency_id: [currency.id, Validators.required],
        variant_id: [variant_id],
        type: ['retail_price'],
        amount: [retail_amount, Validators.min(0)],
        name: [currency.name],
        symbol: [currency.symbol],
      }),
      cost: this.fb.group({
        currency_id: [currency.id, Validators.required],
        variant_id: [variant_id],
        type: ['cost'],
        amount: [cost_amount, Validators.min(0)],
        name: [currency.name],
        symbol: [currency.symbol],
      }),
    });

    this.variants.forEach((variant) => {
      const currencyExists = this.addedCurrencies.some((c) => c.variantId === variant.id && c.data.id === currency.id);
      if (!currencyExists) {
        const retailPrice = variant.currency_prices
          .find((p: ICurrencyPrice) => p.currency_id === currency.id && p.type === 'retail_price')?.amount || null;

        const costPrice = variant.currency_prices
          .find((p: ICurrencyPrice) => p.currency_id === currency.id && p.type === 'cost')?.amount || null;
        this.addedCurrencies.push({
          data: currency,
          variantId: variant.id,
          controls: createControls(currency, variant.id, retailPrice, costPrice),
        });
      }
    });
    this.availableCurrencies = this.availableCurrencies.filter((c) => c.id !== currency.id);
    this.showCurrencyDropdown = false;
    if(!this.currenciesToDisplay.map(c => c.currency_id).includes(currency.id)) this.currenciesToDisplay.push({
      currency_id: currency.id,
      currency_name: currency.name,
      symbol: currency.symbol,
    });
  }

  removeCurrency(currencyId: number): void {
    const index = this.addedCurrencies.findIndex(c => c.data.id === currencyId);
    if (index > -1) {
      this.availableCurrencies.push(this.addedCurrencies[index].data);
      this.addedCurrencies = this.addedCurrencies.filter(c => c.data.id !== currencyId);
    }
    this.currenciesToDisplay = this.currenciesToDisplay.filter(c => c.currency_id !== currencyId);
  }

  openCurrenciesDropdown(event: MouseEvent): void {
    const button = event.currentTarget as HTMLElement;
    const rect = button.getBoundingClientRect();

    this.currenciesDropdown = {
      top: rect.bottom + window.scrollY,
      left: rect.left + window.scrollX,
      minWidth: rect.width,
    };

    this.showCurrencyDropdown = true;
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    const clickedInsideDropdown = this.dropdownRef?.nativeElement.contains(event.target);
    const clickedButton = this.buttonRef?.nativeElement.contains(event.target);

    if (!clickedInsideDropdown && !clickedButton) {
      this.showCurrencyDropdown = false;
    }
  }
}
